import { serverData } from 'utils/server-data'
import { getQueryParam } from 'utils/query'
import { PRODUCT_NAMES } from 'universal/cfs-card'

const { BUSINESS_DEBIT_CARD, CONSUMER_DEBIT_CARD, CONSUMER_VIRTUAL_DEBIT_CARD } = PRODUCT_NAMES

export const CONSOLIDATION_EXPERIMENT_US = 'debitcardnodeweb_consolidation_US'
export const CONSOLIDATION_EXPERIMENT_NON_US = 'debitcardnodeweb_consolidation_non_US'

export const CONSOLIDATION_TREATMENT_US = `Trmt_${CONSOLIDATION_EXPERIMENT_US}`
export const CONSOLIDATION_TREATMENT_NON_US = `Trmt_${CONSOLIDATION_EXPERIMENT_NON_US}`

/** Explicit PPDC product query — must win over account-level isBdmcExperience. */
export function isExplicitConsumerProductName(productName?: string | null): boolean {
  return productName === CONSUMER_DEBIT_CARD || productName === CONSUMER_VIRTUAL_DEBIT_CARD
}

function normalizeTreatmentIds(treatmentIds: unknown): string[] {
  if (Array.isArray(treatmentIds)) {
    return treatmentIds
  }

  if (typeof treatmentIds === 'string') {
    return treatmentIds
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean)
  }

  return []
}

function getBdmcTreatments(): string[] {
  const experiments = serverData.experiments || {}

  if (Array.isArray(experiments.treatments) && experiments.treatments.length > 0) {
    return experiments.treatments
  }

  return normalizeTreatmentIds(experiments.treatmentIds)
}

function isUsLocality(): boolean {
  // eslint-disable-next-line i18n-scan/no-country-dependency -- consolidation ramp is split by US vs non-US Elmo flags
  return String(serverData?.locality?.country || '').toUpperCase() === 'US'
}

function getConsolidationTreatmentNamesForLocality(): string[] {
  const experimentName = isUsLocality()
    ? CONSOLIDATION_EXPERIMENT_US
    : CONSOLIDATION_EXPERIMENT_NON_US

  return [`Trmt_${experimentName}`, experimentName]
}

export function isBdmcConsolidationEnabled(): boolean {
  const treatments = getBdmcTreatments()
  const expectedTreatments = getConsolidationTreatmentNamesForLocality()

  if (treatments.some((treatment) => expectedTreatments.includes(treatment))) {
    return true
  }

  // Fallback: accept either regional consolidation treatment. Server HTML gates use
  // req.locality while serverData.locality comes from req.locale — a mismatch used to
  // false-negative the client check under flag ON and bounce WEB UCS↔dcnw (blank page).
  const anyConsolidationTreatment = [
    CONSOLIDATION_TREATMENT_US,
    CONSOLIDATION_TREATMENT_NON_US,
    CONSOLIDATION_EXPERIMENT_US,
    CONSOLIDATION_EXPERIMENT_NON_US,
  ]
  return treatments.some((treatment) => anyConsolidationTreatment.includes(treatment))
}

/**
 * Whether this client request should be treated as BDMC for consolidation gating.
 * Explicit consumer productName overrides account-level isBdmcExperience (addBdmcLocals).
 */
export function isBdmcGatedClientRequest(): boolean {
  const productName = getQueryParam('productName')
  if (isExplicitConsumerProductName(productName)) {
    return false
  }

  return serverData.isBdmcExperience === true || productName === BUSINESS_DEBIT_CARD
}

/** Client-side guard for hard-coded BDMC deep links (mirrors server consolidation gate). */
export function bdmcConsolidationFlowEligibilityCheck(): boolean {
  if (!isBdmcGatedClientRequest()) {
    return true
  }

  return isBdmcConsolidationEnabled()
}

/**
 * Shared /summary and /rewards mount Bdmc* vs PPDC homes. Explicit CONSUMER / virtual
 * productName always gets the PPDC home even when account type sets isBdmcExperience.
 */
export function shouldUseBdmcSummaryOrRewardsHome(): boolean {
  if (!isBdmcConsolidationEnabled()) {
    return false
  }

  const productName = getQueryParam('productName')
  if (isExplicitConsumerProductName(productName)) {
    return false
  }

  return serverData.isBdmcExperience === true || productName === BUSINESS_DEBIT_CARD
}
